/**
 * Multi-layer image cache system for optimal performance
 * Layer 1: Memory cache (fastest)
 * Layer 2: LocalStorage cache (persistent)
 * Layer 3: Browser HTTP cache (native)
 */

const CACHE_KEY = 'tatva_image_cache';
const CACHE_VERSION = 'v1';
const MAX_CACHE_SIZE = 500; // Maximum number of URLs to cache
const CACHE_EXPIRY_DAYS = 7; // Cache expiry in days

interface CacheEntry {
  url: string;
  timestamp: number;
  version: string;
}

interface ImageCacheData {
  version: string;
  entries: CacheEntry[];
}

// Layer 1: In-memory cache for fastest access
const memoryCache = new Map<string, boolean>();

// Layer 2: Persistent cache using localStorage
class PersistentImageCache {
  private cacheKey: string;

  constructor() {
    this.cacheKey = CACHE_KEY;
    this.initializeCache();
  }

  private initializeCache(): void {
    try {
      const cached = this.getCacheData();
      
      // Validate cache version
      if (cached.version !== CACHE_VERSION) {
        this.clearCache();
        return;
      }

      // Load valid entries into memory cache
      const now = Date.now();
      const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      
      cached.entries.forEach(entry => {
        if (now - entry.timestamp < expiryTime) {
          memoryCache.set(entry.url, true);
        }
      });

      // Clean expired entries
      this.cleanExpiredEntries();
    } catch (error) {
      console.error('Error initializing image cache:', error);
      this.clearCache();
    }
  }

  private getCacheData(): ImageCacheData {
    try {
      const data = localStorage.getItem(this.cacheKey);
      if (data) {
        return JSON.parse(data);
      }
    } catch (error) {
      console.error('Error reading cache data:', error);
    }
    
    return {
      version: CACHE_VERSION,
      entries: []
    };
  }

  private saveCacheData(data: ImageCacheData): void {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
    } catch (error) {
      // If localStorage is full, clear old entries and retry
      console.warn('LocalStorage full, clearing old cache entries');
      this.clearCache();
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify(data));
      } catch (retryError) {
        console.error('Failed to save cache data:', retryError);
      }
    }
  }

  private cleanExpiredEntries(): void {
    const cached = this.getCacheData();
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    const validEntries = cached.entries.filter(entry => {
      const isValid = now - entry.timestamp < expiryTime;
      if (!isValid) {
        memoryCache.delete(entry.url);
      }
      return isValid;
    });

    // Limit cache size
    if (validEntries.length > MAX_CACHE_SIZE) {
      // Keep most recent entries
      const entriesToKeep = validEntries
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, MAX_CACHE_SIZE);
      
      // Remove old entries from memory cache
      validEntries.slice(MAX_CACHE_SIZE).forEach(entry => {
        memoryCache.delete(entry.url);
      });

      this.saveCacheData({
        version: CACHE_VERSION,
        entries: entriesToKeep
      });
    } else if (validEntries.length !== cached.entries.length) {
      this.saveCacheData({
        version: CACHE_VERSION,
        entries: validEntries
      });
    }
  }

  has(url: string): boolean {
    // Check memory cache first (fastest)
    if (memoryCache.has(url)) {
      return true;
    }

    // Check persistent cache
    const cached = this.getCacheData();
    const entry = cached.entries.find(e => e.url === url);
    
    if (entry) {
      const now = Date.now();
      const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      
      if (now - entry.timestamp < expiryTime) {
        // Add to memory cache for faster future access
        memoryCache.set(url, true);
        return true;
      }
    }

    return false;
  }

  set(url: string): void {
    // Add to memory cache
    memoryCache.set(url, true);

    // Add to persistent cache
    const cached = this.getCacheData();
    const existingIndex = cached.entries.findIndex(e => e.url === url);

    if (existingIndex >= 0) {
      // Update timestamp
      cached.entries[existingIndex].timestamp = Date.now();
    } else {
      // Add new entry
      cached.entries.push({
        url,
        timestamp: Date.now(),
        version: CACHE_VERSION
      });
    }

    this.saveCacheData(cached);
  }

  clearCache(): void {
    memoryCache.clear();
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  getCacheStats(): { size: number; memorySize: number; oldestEntry: number | null } {
    const cached = this.getCacheData();
    const oldestEntry = cached.entries.length > 0
      ? Math.min(...cached.entries.map(e => e.timestamp))
      : null;

    return {
      size: cached.entries.length,
      memorySize: memoryCache.size,
      oldestEntry
    };
  }
}

// Singleton instance
const imageCache = new PersistentImageCache();

// Export cache interface
export const ImageCache = {
  has: (url: string) => imageCache.has(url),
  set: (url: string) => imageCache.set(url),
  clear: () => imageCache.clearCache(),
  getStats: () => imageCache.getCacheStats(),
};

/**
 * Preload image and add to cache
 * Uses browser's native cache + our persistent cache
 */
export const preloadAndCacheImage = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already cached
    if (ImageCache.has(url)) {
      resolve();
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      ImageCache.set(url);
      resolve();
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };

    // Set crossOrigin if needed for CORS
    if (url.startsWith('http') && !url.startsWith(window.location.origin)) {
      img.crossOrigin = 'anonymous';
    }

    img.src = url;

    // If image is already complete (browser cache), resolve immediately
    if (img.complete && img.naturalHeight > 0) {
      ImageCache.set(url);
      resolve();
    }
  });
};

/**
 * Check if image is in browser cache
 */
export const isImageInBrowserCache = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    
    const timeout = setTimeout(() => {
      resolve(false);
    }, 100); // Quick timeout for cache check

    img.onload = () => {
      clearTimeout(timeout);
      resolve(img.complete && img.naturalHeight > 0);
    };

    img.onerror = () => {
      clearTimeout(timeout);
      resolve(false);
    };

    img.src = url;

    // If already complete, it's in browser cache
    if (img.complete && img.naturalHeight > 0) {
      clearTimeout(timeout);
      resolve(true);
    }
  });
};

export default ImageCache;

